import { formatDate } from '@seen/core/dates'
import { featureFlags } from '@seen/core/featureFlags'
import { ExtendedComposition } from '@seen/core/models/compositions/composition'
import { ExtendedMedicationRequest } from '@seen/core/models/medicationRequest'
import { referenceStringToId } from '@seen/core/references'
import {
  Button,
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@seen/ui'
import {
  IconPrescription,
  IconReload,
  IconSignature,
  IconX,
} from '@tabler/icons-react'
import type { NodeViewProps } from '@tiptap/react'
import { NodeViewWrapper } from '@tiptap/react'
import { sortBy, uniqBy } from 'lodash-es'
import type { JSX } from 'react'
import { memo, useCallback, useContext, useEffect, useMemo } from 'react'
import { toast } from 'sonner'
import { ContextMenuMedplumLinkItem } from '#src/components/ContextMenuMedplumLinkItem.tsx'
import { HoverCard } from '#src/components/core/hover-card/HoverCard.tsx'
import { HoverCardContent } from '#src/components/core/hover-card/HoverCardContent.tsx'
import { HoverCardTrigger } from '#src/components/core/hover-card/HoverCardTrigger.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { MedicationHistoryTooltip } from '#src/components/medications/MedicationHistoryTooltip.tsx'
import { MedicationName } from '#src/components/medications/MedicationName.tsx'
import type { EncounterMedicationsBlockNodeOptions } from '#src/components/rich-text-editor/EncounterMedicationsBlockNode/EncounterMedicationsBlockNode.tsx'
import { CompositionContext } from '#src/contexts/CompositionContext.tsx'
import { useElationDraftPrescription } from '#src/hooks/useElationDraftPrescription.ts'
import { useElationPrescribe } from '#src/hooks/useElationPrescribe.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import {
  combineDraftIdsByThread,
  getMedicationThreadId,
  medicationForDraftOpen,
  useMedicationDraftsByThread,
} from '#src/hooks/useMedicationDraftsByThread.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { useSearch } from '#src/hooks/useSearch.ts'

interface BuildMedicationMapsResult {
  priorByMedId: Map<string, ExtendedMedicationRequest>
  groupedMedicationByMedId: Map<string, ExtendedMedicationRequest[]>
}

function buildMedicationMaps(
  displayMedications: ExtendedMedicationRequest[],
  allPatientMeds: ExtendedMedicationRequest[] | undefined,
  groupByElationThread: boolean
): BuildMedicationMapsResult {
  const allMedsForGrouping = uniqBy(
    [...displayMedications, ...(allPatientMeds ?? [])],
    (med) => med.id
  )
  const groups = ExtendedMedicationRequest.groupMedications(
    allMedsForGrouping,
    {
      groupByElationThread,
    }
  )
  const priorMap = new Map<string, ExtendedMedicationRequest>()
  const groupedMap = new Map<string, ExtendedMedicationRequest[]>()

  for (const group of groups) {
    for (let i = 0; i < group.requests.length - 1; i++) {
      priorMap.set(group.requests[i].id, group.requests[i + 1])
    }
    for (const request of group.requests) {
      groupedMap.set(request.id, group.requests)
    }
  }

  return { priorByMedId: priorMap, groupedMedicationByMedId: groupedMap }
}

export function EncounterMedicationsBlockView(
  props: NodeViewProps
): JSX.Element {
  const options = props.extension
    .options as EncounterMedicationsBlockNodeOptions
  const isEditable = props.editor.isEditable
  const patientReferenceString = options.patientReferenceString

  /* Dev-only signal that the host editor / renderer forgot to thread the
   * participant through. In production this branch silently renders the
   * "No patient selected" empty state — see `EncounterMedicationsBlockContent`
   * for the visible behavior. */
  useEffect(() => {
    if (
      process.env.NODE_ENV !== 'production' &&
      patientReferenceString === undefined
    ) {
      console.warn(
        '[EncounterMedicationsBlockNode] mounted without `patientReferenceString`. ' +
          'Pass `participant` to `CollaborativeEditor` / `RichTextEditor` / `RichText` ' +
          '(the host applies it via `.configure({ patientReferenceString })`).'
      )
    }
  }, [patientReferenceString])

  return (
    <NodeViewWrapper as="div">
      <EncounterMedicationsData
        patientReferenceString={patientReferenceString}
        isEditable={isEditable}
      />
    </NodeViewWrapper>
  )
}

const EncounterMedicationsData = memo(function EncounterMedicationsData({
  patientReferenceString,
  isEditable,
}: {
  patientReferenceString?: string
  isEditable: boolean
}): JSX.Element {
  const compositionReferenceString = useContext(CompositionContext)

  const { data: composition, isLoading: isLoadingComposition } = useRead(
    ExtendedComposition,
    compositionReferenceString
  )

  const encounterReferenceString = composition?.encounter?.reference

  /* Meds linked to current encounter */
  const { data: encounterMeds, isLoading: isLoadingEncounter } = useSearch(
    ExtendedMedicationRequest,
    patientReferenceString && encounterReferenceString
      ? ExtendedMedicationRequest.queryByEncounter(
          patientReferenceString,
          encounterReferenceString
        )
      : undefined
  )

  const {
    data: stoppedCurrentEncounterMeds,
    isLoading: isLoadingStoppedCurEnc,
  } = useSearch(
    ExtendedMedicationRequest,
    patientReferenceString && encounterReferenceString
      ? ExtendedMedicationRequest.queryStoppedByEncounter(
          patientReferenceString,
          encounterReferenceString
        )
      : undefined
  )

  /* All patient meds for grouping context (change descriptions) */
  const { data: allPatientMeds, isLoading: isLoadingAllMeds } = useSearch(
    ExtendedMedicationRequest,
    patientReferenceString
      ? ExtendedMedicationRequest.queryAllBySubject(patientReferenceString)
      : undefined
  )

  const displayMedications = useMemo(() => {
    const all = [
      ...(encounterMeds ?? []),
      ...(stoppedCurrentEncounterMeds ?? []),
    ]
    const deduped = uniqBy(all, (med) => med.id)
    return sortBy(deduped, (med) => med.authoredOn ?? '').reverse()
  }, [encounterMeds, stoppedCurrentEncounterMeds])

  const groupByElationThread = useFeatureFlag(
    featureFlags.medicationGroupByElationThread
  )
  const { priorByMedId, groupedMedicationByMedId } = useMemo(
    () =>
      buildMedicationMaps(
        displayMedications,
        allPatientMeds,
        groupByElationThread
      ),
    [displayMedications, allPatientMeds, groupByElationThread]
  )

  const { mutateAsync: patchMedication } =
    usePatchMutation<ExtendedMedicationRequest>()

  const handleRemove = useCallback(
    async (med: ExtendedMedicationRequest) => {
      if (!encounterReferenceString) {
        toast.error('Link an appointment to this note to update medications')
        return
      }

      try {
        await patchMedication({
          instance: med,
          mutateFn: (mr) => {
            if (mr.status === 'stopped') {
              mr.stoppedEncounter = undefined
            } else {
              mr.encounter = undefined
            }
          },
        })
      } catch {
        toast.error('Failed to remove medication')
      }
    },
    [encounterReferenceString, patchMedication]
  )

  const patientId = patientReferenceString
    ? referenceStringToId(patientReferenceString)
    : undefined

  const {
    openPrescribe,
    canPrescribe,
    isLoading: isPrescribeLoading,
  } = useElationPrescribe(patientId)

  const {
    createDraftPrescription,
    openDraftPrescription,
    draftingMedicationIds,
    optimisticDraftIdsByThreadId,
    canDraft,
  } = useElationDraftPrescription(patientId, { surface: 'encounter_meds' })
  const { draftByThreadId } = useMedicationDraftsByThread(
    patientReferenceString
  )
  const openStampedDraft = useCallback(
    (draftId: number | string, medication?: ExtendedMedicationRequest) => {
      openDraftPrescription(
        draftId,
        medicationForDraftOpen({
          draftId,
          medication,
          draftsByThreadId: draftByThreadId,
        })
      )
    },
    [draftByThreadId, openDraftPrescription]
  )
  const draftIdsByThreadId = useMemo(
    () =>
      combineDraftIdsByThread({
        serverDraftsByThreadId: draftByThreadId,
        optimisticDraftIdsByThreadId,
      }),
    [draftByThreadId, optimisticDraftIdsByThreadId]
  )

  const isLoading =
    isLoadingComposition ||
    isLoadingEncounter ||
    isLoadingStoppedCurEnc ||
    isLoadingAllMeds

  return (
    <EncounterMedicationsBlockContent
      medications={displayMedications}
      priorByMedId={priorByMedId}
      groupedMedicationByMedId={groupedMedicationByMedId}
      encounterReferenceString={encounterReferenceString}
      onRemove={isEditable ? handleRemove : undefined}
      isLoading={isLoading}
      hasPatient={!!patientReferenceString}
      isEditable={isEditable}
      canPrescribe={canPrescribe}
      onPrescribe={openPrescribe}
      isPrescribeLoading={isPrescribeLoading}
      onDraftPrescription={canDraft ? createDraftPrescription : undefined}
      onOpenDraftPrescription={openStampedDraft}
      draftIdsByThreadId={draftIdsByThreadId}
      draftingMedicationIds={draftingMedicationIds}
    />
  )
})

export interface EncounterMedicationsBlockContentProps {
  medications: ExtendedMedicationRequest[]
  priorByMedId?: Map<string, ExtendedMedicationRequest>
  groupedMedicationByMedId?: Map<string, ExtendedMedicationRequest[]>
  encounterReferenceString?: string
  onRemove?: (med: ExtendedMedicationRequest) => void | Promise<void>
  isLoading: boolean
  hasPatient: boolean
  isEditable: boolean
  canPrescribe?: boolean
  onPrescribe?: () => void
  isPrescribeLoading?: boolean
  onDraftPrescription?: (
    medication: ExtendedMedicationRequest,
    orderType: 'Refill'
  ) => void
  onOpenDraftPrescription?: (
    draftId: number | string,
    medication: ExtendedMedicationRequest
  ) => void
  draftIdsByThreadId?: ReadonlyMap<string, number | string>
  draftingMedicationIds?: ReadonlySet<string>
}

export function EncounterMedicationsBlockContent({
  medications,
  priorByMedId,
  groupedMedicationByMedId,
  encounterReferenceString,
  onRemove,
  isLoading,
  hasPatient,
  isEditable,
  canPrescribe,
  onPrescribe,
  isPrescribeLoading,
  onDraftPrescription,
  onOpenDraftPrescription,
  draftIdsByThreadId,
  draftingMedicationIds,
}: EncounterMedicationsBlockContentProps): JSX.Element {
  return (
    <div className="flex flex-col rounded-t-md border">
      <div className="flex h-[50px] items-center gap-2 rounded-t-md border-b bg-frame px-4">
        <div className="flex flex-1 items-center justify-between gap-2">
          <div className="flex w-fit items-center gap-2 text-sm leading-snug font-medium">
            Medications Ordered This Visit
            {!isLoading && hasPatient && (
              <span className="font-normal text-muted-foreground">
                {medications.length}
              </span>
            )}
          </div>
          {canPrescribe && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={onPrescribe}
                  loading={isPrescribeLoading}
                >
                  <IconPrescription className="size-4" />
                  Prescribe
                </Button>
              </TooltipTrigger>
              <TooltipContent>Prescribe in Elation</TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
      <div className="flex flex-1 flex-col gap-1 px-4 py-2">
        {isLoading ? (
          <Loader />
        ) : !hasPatient ? (
          <div className="text-sm text-muted-foreground">
            No patient selected
          </div>
        ) : medications.length === 0 ? (
          <div className="text-sm text-muted-foreground">No medications</div>
        ) : (
          <div className="flex flex-col gap-1">
            {medications.map((med) => {
              const prior = priorByMedId?.get(med.id)
              const groupedMedicationRequests = groupedMedicationByMedId?.get(
                med.id
              ) ?? [med]
              const medThreadId = getMedicationThreadId(med)
              const draftId = medThreadId
                ? draftIdsByThreadId?.get(medThreadId)
                : undefined
              const isCreatingDraft =
                draftingMedicationIds?.has(med.id) ?? false
              const showSignAction =
                onOpenDraftPrescription !== undefined && draftId !== undefined
              const isStopped = med.status === 'stopped'
              /* Show "Stopped" for meds stopped in this encounter OR
               * legacy stopped meds with no stoppedEncounter tag (pre-migration data). */
              const isStoppedForThisEncounter =
                isStopped &&
                (!med.stoppedEncounter ||
                  med.stoppedEncounter.reference === encounterReferenceString)

              let description: string | undefined
              if (isStoppedForThisEncounter) {
                description = 'Stopped'
              } else if (prior) {
                const changes = getMedicationChanges(med, prior)
                description =
                  changes.length > 0 ? changes.join(' · ') : 'Refill'
              }

              const medLabel = (
                <MedicationName
                  display={med.medicationDisplay ?? 'Unknown Medication'}
                  doseSummary={med.doseSummary}
                />
              )

              return (
                <ContextMenu key={med.id}>
                  <ContextMenuTrigger asChild>
                    <div className="group/row flex items-start gap-2 py-1 text-sm">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <HoverCard openDelay={200} closeDelay={100}>
                            <HoverCardTrigger asChild>
                              <span className="font-medium">{medLabel}</span>
                            </HoverCardTrigger>
                            <HoverCardContent
                              className="w-[420px] p-3"
                              side="bottom"
                              align="start"
                            >
                              <MedicationHistoryTooltip
                                medicationDisplay={medLabel}
                                medicationRequests={groupedMedicationRequests}
                                actionRequestId={med.id}
                                onDraftPrescription={onDraftPrescription}
                                onOpenDraftPrescription={
                                  onOpenDraftPrescription
                                }
                                draftId={draftId}
                                isCreatingDraft={isCreatingDraft}
                              />
                            </HoverCardContent>
                          </HoverCard>
                          {med.authoredOn && (
                            <span className="text-xs text-muted-foreground">
                              {formatDate(med.authoredOn)}
                            </span>
                          )}
                        </div>
                        {description && (
                          <div className="text-xs text-muted-foreground">
                            {description}
                          </div>
                        )}
                      </div>
                      {isEditable && onRemove && (
                        <div className="flex gap-0.5 opacity-0 transition-opacity group-hover/row:opacity-100">
                          <Button
                            variant="ghost"
                            size="xs"
                            onClick={() => onRemove(med)}
                          >
                            <IconX className="size-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </ContextMenuTrigger>
                  <ContextMenuContent>
                    {showSignAction && draftId !== undefined && (
                      <ContextMenuItem
                        onClick={() => onOpenDraftPrescription?.(draftId, med)}
                      >
                        <IconSignature className="size-4" />
                        Sign
                      </ContextMenuItem>
                    )}
                    {!showSignAction &&
                      onDraftPrescription &&
                      !med.refillUnavailableReason && (
                        <ContextMenuItem
                          onClick={() => onDraftPrescription(med, 'Refill')}
                          disabled={isCreatingDraft}
                        >
                          <IconReload className="size-4" />
                          Refill
                        </ContextMenuItem>
                      )}
                    <ContextMenuMedplumLinkItem to={med} />
                  </ContextMenuContent>
                </ContextMenu>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

function getMedicationChanges(
  current: ExtendedMedicationRequest,
  prior: ExtendedMedicationRequest
): string[] {
  const changes: string[] = []

  if (
    current.medicationDisplay !== prior.medicationDisplay &&
    prior.medicationDisplay
  ) {
    changes.push(`was ${prior.medicationDisplay}`)
  }

  const currentDosage = current.dosageInstructions?.join('; ')
  const priorDosage = prior.dosageInstructions?.join('; ')
  if (currentDosage !== priorDosage) {
    if (priorDosage && currentDosage) {
      changes.push(`dosage: ${priorDosage} → ${currentDosage}`)
    } else if (currentDosage) {
      changes.push(`added dosage: ${currentDosage}`)
    }
  }

  if (current.status !== prior.status) {
    changes.push(`${prior.statusText} → ${current.statusText}`)
  }

  const currentQty = current.dispenseRequest?.quantity?.value
  const priorQty = prior.dispenseRequest?.quantity?.value
  if (currentQty !== priorQty && (currentQty || priorQty)) {
    changes.push(`qty: ${priorQty ?? '–'} → ${currentQty ?? '–'}`)
  }

  if (current.refills !== prior.refills) {
    changes.push(`refills: ${prior.refills ?? '–'} → ${current.refills ?? '–'}`)
  }

  return changes
}
